import { useNavigate } from 'react-router';
import { PageHead } from 'ui/kit/PageHead';
import { Bell, BellDot } from 'lucide-react';

import { FadeIn } from 'ui/kit/uiMotion';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type UserNotification,
} from 'hooks/useNotifications';
import { getNotificationPath } from 'components/NotificationBell/NotificationBell';

import s4 from './s4Screen.module.scss';
import classes from './NotificationsPage.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const actionLabel = (item: UserNotification) => {
  const type = item.notification_type;
  if (type === 'tutor_bulletin_response' || type === 'bulletin_available') return 'Посмотреть отклик';
  if (type === 'student_invitation' || type === 'chat_blocked' || type.startsWith('chat')) {
    return 'Перейти в чат';
  }
  if (type.startsWith('moderation_') || type.startsWith('bulletin_moderation_')) {
    return 'Посмотреть профиль';
  }
  if (type === 'review_request') return 'Оставить отзыв';
  return 'Открыть';
};

export const NotificationsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const items = data?.items ?? [];
  const unread = data?.unread_count ?? 0;

  const openItem = (item: UserNotification) => {
    if (!item.is_read) markRead.mutate(item.id);
    const path = getNotificationPath(item, 'student');
    if (path) navigate(path);
  };

  return (
    <FadeIn>
      <main className={s4.page}>
        <PageHead crumb={<>Личный кабинет / Уведомления</>} title={<>Уведомления</>} />
        <section className={s4.card}>
          <h2 className={s4.cardTitle}>События личного кабинета</h2>
          <p className={s4.muted}>
            Сообщения о задачах, чатах и проверке. Звук и всплывающие окна включаются в настройках.
          </p>
          {isLoading ? <BrandLoader center label='Загрузка...' /> : null}
          {!isLoading && items.length === 0 ? (
            <p className={s4.body}>Пока нет уведомлений</p>
          ) : null}
          <ul className={classes.list}>
            {items.map((item) => {
              const path = getNotificationPath(item, 'student');
              return (
                <li key={item.id} className={item.is_read ? classes.card : classes.cardUnread}>
                  <span className={classes.itemIcon} aria-hidden>
                    {item.is_read ? <Bell size={20} /> : <BellDot size={20} />}
                  </span>
                  <div className={classes.itemText}>
                    <p className={classes.itemTitle}>{item.title}</p>
                    <p className={classes.itemBody}>{item.body}</p>
                    <p className={s4.muted}>
                      {item.is_read ? 'Прочитано' : 'Не прочитано'}
                      {formatDate(item.created_at) ? ` · ${formatDate(item.created_at)}` : ''}
                    </p>
                  </div>
                  {path ? (
                    <button type='button' className={classes.itemAction} onClick={() => openItem(item)}>
                      {actionLabel(item)}
                    </button>
                  ) : !item.is_read ? (
                    <button
                      type='button'
                      className={classes.itemAction}
                      onClick={() => markRead.mutate(item.id)}
                    >
                      Отметить прочитанным
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {unread > 0 ? (
            <button
              type='button'
              className={s4.secondary}
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              Отметить все прочитанными
            </button>
          ) : null}
        </section>
      </main>
    </FadeIn>
  );
};
